/**
 * Hidratacao de blocos a partir de PublicPageContent legado.
 *
 * Usado quando o tenant ainda nao tem `blocks` definidos: gera uma lista
 * default que reproduz o layout dos templates antigos. Mantem compat 100%
 * pra tenants pre-Fase 1.
 */

import type {
  Block,
  PublicPageConfig,
  SantinhoTemplate,
} from '@/types/tenant.types';

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Gera blocks default a partir do conteudo legado. Ordem segue a
 * "narrativa" de campanha: Hero → Bio → Mandatos → Propostas → Conquistas
 * → Projetos aprovados → Galeria → Social.
 *
 * Blocos cujos dados estao vazios sao incluidos como `visible: false` para
 * que apareca como skeleton no editor mas nao renderiza no publico.
 */
export function hydrateBlocksFromContent(publicPage: PublicPageConfig): Block[] {
  const content = publicPage.content || {};
  const blocks: Block[] = [];
  let order = 0;

  const push = (b: Omit<Block, 'order'>) => {
    blocks.push({ ...b, order: order++ });
  };

  // Hero — sempre presente, mesmo vazio
  push({
    id: uid('hero'),
    type: 'hero',
    visible: true,
    props: {
      layout: 'centered',
      showNumber: !!content.candidateNumber,
    },
  });

  // Bio
  if (content.biography?.trim()) {
    push({
      id: uid('bio'),
      type: 'bio',
      visible: true,
      props: { title: 'Sobre' },
    });
  }

  // Mandates
  if (content.mandates && content.mandates.length > 0) {
    push({
      id: uid('mandates'),
      type: 'mandates',
      visible: true,
      props: { title: 'Trajetória política' },
    });
  }

  // Proposals
  if (content.proposals && content.proposals.length > 0) {
    push({
      id: uid('proposals'),
      type: 'proposals',
      visible: true,
      props: { title: 'Propostas', columns: 2 },
      style: { background: 'accent' },
    });
  }

  // Achievements
  if (content.achievements && content.achievements.length > 0) {
    push({
      id: uid('achievements'),
      type: 'achievements',
      visible: true,
      props: { title: 'Conquistas' },
    });
  }

  // Approved projects
  if (content.approvedProjects && content.approvedProjects.length > 0) {
    push({
      id: uid('approved-projects'),
      type: 'approved-projects',
      visible: true,
      props: { title: 'Projetos aprovados', variant: 'cards' },
      style: { background: 'accent' },
    });
  }

  // Gallery
  if (content.gallery && content.gallery.length > 0) {
    push({
      id: uid('gallery'),
      type: 'gallery',
      visible: true,
      props: { title: 'Galeria' },
    });
  }

  // Social
  const hasSocial = Object.values(publicPage.socialLinks || {}).some((v) => !!v);
  if (hasSocial) {
    push({
      id: uid('social'),
      type: 'social',
      visible: true,
      props: { title: 'Me siga nas redes' },
      style: { background: 'accent', alignment: 'center' },
    });
  }

  return blocks;
}

/**
 * Aplica template legado como ajuste de style/layout nos blocos hidratados.
 * Usado quando o tenant tem `template` legado mas ainda nao migrou para `blocks`.
 */
export function applyLegacyTemplateStyle(
  blocks: Block[],
  template: SantinhoTemplate | undefined
): Block[] {
  if (!template) return blocks;

  return blocks.map((block) => {
    if (block.type === 'hero') {
      const props = { ...block.props } as { layout?: string };
      if (template === 'bold' || template === 'campaign') {
        props.layout = 'centered';
      } else if (template === 'classic' || template === 'minimal') {
        props.layout = 'split';
      }
      return { ...block, props };
    }
    return block;
  });
}

/**
 * Pega os blocks efetivos para renderizacao. Se ja existem blocks definidos,
 * usa-os. Senao, hidrata a partir do conteudo legado.
 */
export function resolveBlocks(publicPage: PublicPageConfig): Block[] {
  if (publicPage.blocks && publicPage.blocks.length > 0) {
    return [...publicPage.blocks].sort((a, b) => a.order - b.order);
  }
  const hydrated = hydrateBlocksFromContent(publicPage);
  return applyLegacyTemplateStyle(hydrated, publicPage.template);
}
